import { useEffect, useState } from 'react';

/**
 * Tracks a CSS media query (e.g. `(min-width: 1024px)`) reactively via `window.matchMedia`.
 * Used for the two/three-pane inbox layout (§ responsive inbox) instead of pure-CSS
 * show/hide, so components can decide *what to render* (skip mounting the contact panel
 * below the breakpoint) rather than just hiding it — and so it is testable by mocking
 * `matchMedia` in vitest/RTL.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', listener);
      return () => mql.removeEventListener('change', listener);
    }
    // Safari < 14 fallback.
    mql.addListener(listener);
    return () => mql.removeListener(listener);
  }, [query]);

  return matches;
}

/** Breakpoints for the two-pane inbox (match Tailwind's default `lg`/`xl`). */
export const BP_LG = '(min-width: 1024px)';
export const BP_XL = '(min-width: 1280px)';
